import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Button from '../shared/TailwindComponents/Button';
import { ColumnDataType, TableColumn, UpdateColumnPayload } from '../../types/batchTables';

interface EditColumnContentProps {
  column: TableColumn;
  onConfirm: (data: UpdateColumnPayload, queryChanged: boolean) => void;
  onCancel: () => void;
}

const dataTypeOptions: { value: ColumnDataType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'currency', label: 'Currency' },
];

const EditColumnContent: React.FC<EditColumnContentProps> = ({ column, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(column.name);
  const [query, setQuery] = useState(column.query || '');
  const [dataType, setDataType] = useState<ColumnDataType>(column.data_type);

  const handleSubmit = useCallback(() => {
    if (!name.trim()) return;

    const payload: UpdateColumnPayload = {};
    let queryChanged = false;

    if (name.trim() !== column.name) {
      payload.name = name.trim();
    }
    if (query.trim() !== (column.query || '')) {
      payload.query = query.trim() || undefined;
      queryChanged = true;
    }
    if (dataType !== column.data_type) {
      payload.data_type = dataType;
      queryChanged = true; // data_type change should also trigger re-process
    }

    // Only call if something changed
    if (Object.keys(payload).length > 0) {
      onConfirm(payload, queryChanged);
    } else {
      onCancel();
    }
  }, [name, query, dataType, column, onConfirm, onCancel]);

  const hasChanges =
    name.trim() !== column.name ||
    query.trim() !== (column.query || '') ||
    dataType !== column.data_type;

  return (
    <div className="w-[480px]">
      <div className="px-6 py-5 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('Column Name')} *
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder={t('e.g., Effective Date')}
            autoFocus
          />
        </div>

        {/* Query */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('AI Query')}
            <span className="text-xs text-gray-400 ml-1">({t('defaults to column name if empty')})</span>
          </label>
          <Textarea
            id="column-query-edit"
            label=""
            value={query}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuery(e.target.value)}
            placeholder={t('What is the effective date or execution date of this document?')}
            rows={3}
          />
        </div>

        {/* Data Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('Data Type')}
          </label>
          <div className="flex flex-wrap gap-2">
            {dataTypeOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDataType(opt.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                  dataType === opt.value
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                {t(opt.label)}
              </button>
            ))}
          </div>
        </div>

        {/* Re-process hint */}
        {(query.trim() !== (column.query || '') || dataType !== column.data_type) && (
          <div className="rounded-md bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {t('The query or data type has changed. After saving, you will be asked whether to re-process this column across all documents.')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!name.trim() || !hasChanges}
        >
          {t('Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default EditColumnContent;
